// @clinic-pairings-tab
// Which clinics this doctor delivers. A pairing IS the statement that they
// work that clinic — no pairing means they do not appear in that clinic's
// funnel and a booking against them is refused. Opt-in by design, so a
// doctor who does not want a clinic simply never gets ticked.
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Card, EmptyState } from './Shared';
import { clinicAPI } from '../services/api';

const euro = (v) => (v === null || v === undefined ? '—' : `€${Number(v).toFixed(2)}`);

// Doctor's share = fee minus DocNow's commission. Shown so the admin sees
// what the doctor actually earns rather than having to work it out.
const doctorShare = (fee, commissionCents) => {
  const f = Number(fee), c = Number(commissionCents);
  if (!Number.isFinite(f) || !Number.isFinite(c)) return null;
  return (f * 100 - c) / 100;
};

export default function DoctorClinicsTab({ doctorId }) {
  const [clinics, setClinics] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);   // serviceTypeId being edited
  const [form, setForm] = useState({ consultationFee: '', commissionCents: '', slotDurationMins: '' });
  const [saving, setSaving] = useState(false);
  const [confirmOff, setConfirmOff] = useState(null);

  const load = async () => {
    try {
      const res = await clinicAPI.listForDoctor(doctorId);
      setClinics(res.data?.data || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Αποτυχία φόρτωσης ιατρείων');
      setClinics([]);
    }
  };

  useEffect(() => {
    if (!doctorId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await clinicAPI.listForDoctor(doctorId);
        if (cancelled) return;
        setClinics(res.data?.data || []);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.error || 'Αποτυχία φόρτωσης ιατρείων');
        setClinics([]);
      }
    })();
    return () => { cancelled = true; };
  }, [doctorId]);

  // Ticking an unpaired clinic cannot be a bare checkbox: a pairing needs a
  // fee, a commission and a duration. Opens the form prefilled with whatever
  // the pairing already had, or blank for a new one.
  const openEditor = (c) => {
    setForm({
      consultationFee: c.consultationFee ?? '',
      commissionCents: c.commissionCents ?? '',
      slotDurationMins: c.slotDurationMins ?? '',
    });
    setEditing(c.serviceTypeId);
  };

  const save = async (serviceTypeId) => {
    const fee = Number(form.consultationFee);
    const commission = Number(form.commissionCents);
    const duration = Number(form.slotDurationMins);

    if (!Number.isFinite(fee) || fee <= 0) return toast.error('Η τιμή πρέπει να είναι θετικός αριθμός');
    if (!Number.isInteger(commission) || commission < 0) return toast.error('Η προμήθεια πρέπει να είναι ακέραιος σε λεπτά');
    if (commission > Math.round(fee * 100)) return toast.error('Η προμήθεια δεν μπορεί να υπερβαίνει την τιμή');
    if (!Number.isInteger(duration) || duration <= 0 || duration % 15 !== 0)
      return toast.error('Η διάρκεια πρέπει να είναι πολλαπλάσιο του 15');

    setSaving(true);
    try {
      await clinicAPI.upsert(doctorId, serviceTypeId, {
        consultationFee: fee,
        commissionCents: commission,
        slotDurationMins: duration,
        isActive: true,
      });
      toast.success('Αποθηκεύτηκε');
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Αποτυχία αποθήκευσης');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (serviceTypeId) => {
    setSaving(true);
    try {
      const res = await clinicAPI.deactivate(doctorId, serviceTypeId);
      const orphaned = res.data?.data?.orphanedSlots || 0;
      if (orphaned > 0) {
        // Those slots still exist but no patient can reach them — the
        // doctor list filters on an active pairing.
        toast(`Απενεργοποιήθηκε. Προσοχή: ${orphaned} δημοσιευμένα ραντεβού για αυτό το ιατρείο δεν είναι πλέον κρατήσιμα.`,
              { duration: 8000, icon: '⚠️' });
      } else {
        toast.success('Απενεργοποιήθηκε');
      }
      setConfirmOff(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Αποτυχία απενεργοποίησης');
    } finally {
      setSaving(false);
    }
  };

  if (error && !clinics?.length) return <Card><EmptyState message={error} /></Card>;
  if (clinics === null) return <Card><EmptyState message="Φόρτωση…" /></Card>;
  if (!clinics.length) return <Card><EmptyState message="Δεν υπάρχουν ιατρεία" /></Card>;

  return (
    <Card>
      <p style={{ fontSize: 13, color: 'var(--slate)', marginTop: 0, marginBottom: 16 }}>
        Ο ιατρός εμφανίζεται μόνο στα ιατρεία που είναι ενεργοποιημένα εδώ.
        Κάθε ιατρείο έχει δική του τιμή και διάρκεια.
      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border, #e5e5e5)' }}>
            <th style={{ padding: '8px 6px', width: 40 }}></th>
            <th style={{ padding: '8px 6px' }}>Ιατρείο</th>
            <th style={{ padding: '8px 6px' }}>Τιμή</th>
            <th style={{ padding: '8px 6px' }}>Ιατρός</th>
            <th style={{ padding: '8px 6px' }}>DocNow</th>
            <th style={{ padding: '8px 6px' }}>Διάρκεια</th>
            <th style={{ padding: '8px 6px' }}></th>
          </tr>
        </thead>
        <tbody>
          {clinics.map((c) => {
            const on = c.pairingIsActive === true;
            const isEditing = editing === c.serviceTypeId;
            const share = doctorShare(c.consultationFee, c.commissionCents);

            return (
              <React.Fragment key={c.serviceTypeId}>
                <tr style={{
                  borderBottom: '1px solid var(--border, #f0f0f0)',
                  // Inactive clinics are greyed, not hidden: pairing doctors
                  // ahead of a launch is a real workflow.
                  opacity: c.clinicIsActive ? 1 : 0.55,
                }}>
                  <td style={{ padding: '10px 6px' }}>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={saving}
                      onChange={() => (on ? setConfirmOff(c.serviceTypeId) : openEditor(c))}
                      style={{ width: 18, height: 18, cursor: saving ? 'default' : 'pointer' }}
                    />
                  </td>
                  <td style={{ padding: '10px 6px', fontWeight: 600 }}>
                    {c.nameEl}
                    {!c.clinicIsActive && (
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: 'var(--slate)' }}>
                        (ανενεργό ιατρείο)
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 6px' }}>{on ? euro(c.consultationFee) : '—'}</td>
                  <td style={{ padding: '10px 6px' }}>{on ? euro(share) : '—'}</td>
                  <td style={{ padding: '10px 6px' }}>{on ? euro(Number(c.commissionCents) / 100) : '—'}</td>
                  <td style={{ padding: '10px 6px' }}>{on ? `${c.slotDurationMins}′` : '—'}</td>
                  <td style={{ padding: '10px 6px', textAlign: 'right' }}>
                    {on && !isEditing && (
                      <button onClick={() => openEditor(c)} disabled={saving}
                              style={{ border: 'none', background: 'transparent', color: 'var(--crimson)',
                                       cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        Επεξεργασία
                      </button>
                    )}
                  </td>
                </tr>

                {isEditing && (
                  <tr>
                    <td colSpan={7} style={{ padding: '12px 6px 18px', background: 'var(--mist, #fafafa)' }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 12 }}>
                          <div style={{ marginBottom: 4, color: 'var(--slate)' }}>Τιμή ασθενούς (€)</div>
                          <input type="number" step="0.01" min="0" value={form.consultationFee}
                                 onChange={(e) => setForm({ ...form, consultationFee: e.target.value })}
                                 style={{ padding: '6px 8px', width: 120, borderRadius: 6,
                                          border: '1px solid var(--border, #ddd)' }} />
                        </label>
                        <label style={{ fontSize: 12 }}>
                          <div style={{ marginBottom: 4, color: 'var(--slate)' }}>Προμήθεια DocNow (λεπτά)</div>
                          <input type="number" step="1" min="0" value={form.commissionCents}
                                 onChange={(e) => setForm({ ...form, commissionCents: e.target.value })}
                                 style={{ padding: '6px 8px', width: 140, borderRadius: 6,
                                          border: '1px solid var(--border, #ddd)' }} />
                        </label>
                        <label style={{ fontSize: 12 }}>
                          <div style={{ marginBottom: 4, color: 'var(--slate)' }}>Διάρκεια (λεπτά)</div>
                          <input type="number" step="15" min="15" value={form.slotDurationMins}
                                 onChange={(e) => setForm({ ...form, slotDurationMins: e.target.value })}
                                 style={{ padding: '6px 8px', width: 110, borderRadius: 6,
                                          border: '1px solid var(--border, #ddd)' }} />
                        </label>
                        <div style={{ fontSize: 12, color: 'var(--slate)', paddingBottom: 8 }}>
                          Ο ιατρός λαμβάνει{' '}
                          <strong>
                            {euro(doctorShare(form.consultationFee, form.commissionCents))}
                          </strong>
                        </div>
                        <button onClick={() => save(c.serviceTypeId)} disabled={saving}
                                style={{ padding: '7px 16px', border: 'none', borderRadius: 6,
                                         background: 'var(--crimson)', color: 'white',
                                         fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                          {saving ? 'Αποθήκευση…' : 'Αποθήκευση'}
                        </button>
                        <button onClick={() => setEditing(null)} disabled={saving}
                                style={{ padding: '7px 12px', border: 'none', borderRadius: 6,
                                         background: 'transparent', color: 'var(--slate)',
                                         fontSize: 12, cursor: 'pointer' }}>
                          Άκυρο
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                {confirmOff === c.serviceTypeId && (
                  <tr>
                    <td colSpan={7} style={{ padding: '12px 6px 18px', background: '#FFF6F6' }}>
                      <div style={{ fontSize: 13, marginBottom: 10 }}>
                        Να αφαιρεθεί ο ιατρός από «{c.nameEl}»; Δεν θα εμφανίζεται πλέον
                        στους ασθενείς για αυτό το ιατρείο. Τα ολοκληρωμένα ραντεβού δεν επηρεάζονται.
                      </div>
                      <button onClick={() => deactivate(c.serviceTypeId)} disabled={saving}
                              style={{ padding: '7px 16px', border: 'none', borderRadius: 6,
                                       background: 'var(--crimson)', color: 'white',
                                       fontWeight: 600, fontSize: 12, cursor: 'pointer', marginRight: 8 }}>
                        {saving ? 'Αφαίρεση…' : 'Ναι, αφαίρεση'}
                      </button>
                      <button onClick={() => setConfirmOff(null)} disabled={saving}
                              style={{ padding: '7px 12px', border: 'none', borderRadius: 6,
                                       background: 'transparent', color: 'var(--slate)',
                                       fontSize: 12, cursor: 'pointer' }}>
                        Άκυρο
                      </button>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
